import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Section } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { Award, Plus, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const REVIEWER_LABEL = { self: "Self", manager: "Manager", hr: "HR", peer: "Peer" };

export default function ReviewDetail() {
  const { cycleId, employeeId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const { data: employee, isLoading: le } = useQuery({ queryKey: ["employee", employeeId], queryFn: () => get(`/hrms/employees/${employeeId}`) });
  const { data: reviews, isLoading: lr } = useQuery({ queryKey: ["cycle-reviews", cycleId, employeeId], queryFn: () => get(`/hrms/performance/cycles/${cycleId}/reviews?employee_id=${employeeId}`) });
  const { data: template } = useQuery({ queryKey: ["employee-template", employeeId], queryFn: () => get(`/hrms/performance/employee-template/${employeeId}`), enabled: !!employeeId });
  const dimMap = useMemo(() => {
    const map = {};
    (template?.dimensions || []).forEach((d) => { map[d.id] = d; });
    return map;
  }, [template]);

  const approve = useMutation({
    mutationFn: () => post(`/hrms/performance/cycles/${cycleId}/employees/${employeeId}/approve`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cycle-reviews", cycleId, employeeId] }); toast.success("Review approved and released to the employee"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not approve"),
  });

  if (le || lr) return <Skeleton className="h-96" />;

  const canReview = (type) => !reviews.some((r) => r.reviewer_type === type) || (type === "peer");
  const isManager = me?.id === employee.manager_id;
  const isHrReviewer = can("hrms", "override");
  const isOwnProfile = me?.id === employeeId;

  const selfR = reviews.find((r) => r.reviewer_type === "self");
  const mgrR = reviews.find((r) => r.reviewer_type === "manager");
  const hrR = reviews.find((r) => r.reviewer_type === "hr");
  const approved = reviews.some((r) => r.approved);
  const canSeeFullSummary = !isOwnProfile || approved;
  const submittedTypes = [selfR, mgrR, hrR].filter(Boolean);

  const combinedRows = template?.dimensions.map((d) => {
    const scores = submittedTypes.map((r) => r.ratings.find((rt) => rt.dimension_id === d.id)?.score).filter((s) => s != null);
    const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    return { ...d, avg };
  }) || [];
  const areasOfImprovement = combinedRows.filter((d) => d.avg != null && d.avg / (d.max_score || 5) < 0.6);
  const overallPct = submittedTypes.length
    ? (submittedTypes.reduce((a, r) => a + r.total_score / r.max_score, 0) / submittedTypes.length) * 100 : null;

  return (
    <div>
      <div className="mb-4 text-xs text-muted-foreground">
        <button onClick={() => navigate("/hrms/performance")} className="hover:underline">Performance</button> / {employee.name}
      </div>
      <div className="mb-6 flex items-center gap-4">
        <Avatar user={employee} size="lg" />
        <div><h1 className="font-display text-2xl font-bold tracking-tight">{employee.name}</h1><div className="text-sm text-muted-foreground">{employee.designation}</div></div>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          {isOwnProfile && canReview("self") && (
            <Button size="sm" data-testid="add-self-review" onClick={() => navigate(`/hrms/performance/review/${cycleId}/${employeeId}/self`)}><Plus className="mr-1.5 h-3.5 w-3.5" />Self-Review</Button>
          )}
          {isManager && canReview("manager") && (
            <Button size="sm" variant="outline" data-testid="add-manager-review" onClick={() => navigate(`/hrms/performance/review/${cycleId}/${employeeId}/manager`)}><Plus className="mr-1.5 h-3.5 w-3.5" />Manager Review</Button>
          )}
          {isHrReviewer && canReview("hr") && (
            <Button size="sm" variant="outline" data-testid="add-hr-review" onClick={() => navigate(`/hrms/performance/review/${cycleId}/${employeeId}/hr`)}><Plus className="mr-1.5 h-3.5 w-3.5" />HR Review</Button>
          )}
          <Button size="sm" variant="outline" data-testid="add-peer-review" onClick={() => navigate(`/hrms/performance/review/${cycleId}/${employeeId}/peer`)}><Plus className="mr-1.5 h-3.5 w-3.5" />Add Peer Review</Button>
          {isHrReviewer && selfR && mgrR && hrR && !approved && (
            <Button size="sm" data-testid="approve-release" onClick={() => approve.mutate()} disabled={approve.isPending}>
              <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />Approve & Release
            </Button>
          )}
        </div>
      </div>

      {!reviews.length ? (
        <EmptyState icon={Award} title="No reviews submitted yet for this cycle" />
      ) : !canSeeFullSummary ? (
        <EmptyState icon={Lock} title="Pending HR approval" description="Your self, manager, and HR reviews are being finalized. You'll see your combined results once HR approves and releases them." />
      ) : (
        <div className="space-y-4">
          {overallPct != null && (
            <Section title="Combined Summary" testid="combined-summary" action={<StatusBadge family="blue" label={`${overallPct.toFixed(1)}%`} />}>
              <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
                {combinedRows.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-xs">
                    <span className="text-muted-foreground">{d.name}</span>
                    <span className="font-semibold">{d.avg != null ? `${d.avg.toFixed(1)} / ${d.max_score || 5}` : "—"}</span>
                  </div>
                ))}
              </div>
              {areasOfImprovement.length > 0 && (
                <div className="border-t border-border/60 px-4 py-3">
                  <div className="mb-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">Areas of Improvement</div>
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {areasOfImprovement.map((d) => <li key={d.id}>• {d.name}</li>)}
                  </ul>
                </div>
              )}
            </Section>
          )}
          {reviews.map((r) => (
            <Section key={r.id} title={`${REVIEWER_LABEL[r.reviewer_type]} Review`} action={<StatusBadge family="blue" label={`${r.total_score} / ${r.max_score}`} />}>
              <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
                {r.ratings.map((rt) => (
                  <div key={rt.dimension_id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-xs">
                    <span className="text-muted-foreground">{dimMap[rt.dimension_id]?.name || rt.dimension_id}</span>
                    <span className="font-semibold">{rt.score}</span>
                  </div>
                ))}
              </div>
              {r.feedback_text && <p className="border-t border-border/60 px-4 py-3 text-sm text-muted-foreground">{r.feedback_text}</p>}
            </Section>
          ))}
        </div>
      )}
    </div>
  );
}
